(function () {
  'use strict';
  const Fit = typeof module !== 'undefined' && module.exports ? require('./fitwarm-fit-engine.js') : window.FitwarmFit;

  function convertMeasurement(text, from, to) {
    if (text === '' || from === to) return text;
    const factors = { lb: 1 / Fit.LB_PER_KG, kg: 1, in: 2.54, cm: 1 };
    return String(Number((Number(text) * factors[from] / factors[to]).toFixed(4)));
  }

  function recommendProduct(chart, product, sizeIndex, input) {
    const measured = input.chestCm !== null && input.chestCm !== undefined;
    const chestCm = measured ? input.chestCm : Fit.bodyChestFromWeight(input.weightKg);
    if (chart.secondary === 'waist' && (!Number.isFinite(input.secondaryCm) || input.secondaryCm <= 0)) throw new Error('Add your dog’s waist measurement for a two-piece outfit.');
    const sizes = new Set(product.variants.map(variant => Fit.normalizeSize(variant.options[sizeIndex])));
    const rows = chart.rows.filter(row => sizes.has(row.size));
    const spread = measured ? 0 : Fit.weightChestSpread(input.chestVariationPercent);
    const waistCm = chart.secondary === 'waist' ? input.secondaryCm : null;
    // Apply uncertainty to the body first, then add garment room at both ends.
    const low = Fit.chestFit(rows, chestCm * (1 - spread), waistCm);
    const high = Fit.chestFit(rows, chestCm * (1 + spread), waistCm);
    const matches = low.row ? rows.slice(rows.indexOf(low.row), high.row ? rows.indexOf(high.row) + 1 : rows.length) : [];
    const beyond = !high.row;
    let note = measured ? 'Chosen by chest girth, with at least 2.5 cm of room.' : 'Weight-based estimates include at least 2.5 cm of chest room. Confirm chest girth before choosing a size, especially for broad-chested or long-bodied dogs.';
    if (chart.secondary === 'waist') note += ' Waist fit includes 2.5 cm of room too.';
    if (beyond) {
      note = measured ? 'Your body measurements plus room exceed this product’s garment sizes. Check another style or contact us for help.' : 'This estimate extends beyond this product’s garment sizes. Add a chest measurement to check the fit.';
    } else if (chart.secondary === 'back') {
      const coverage = matches.map(row => ({ size: row.size, note: Fit.backCoverage(row, input.secondaryCm) })).filter(item => item.note);
      for (const item of coverage) note += ` ${matches.length > 1 ? `${item.size}: ` : ''}${item.note}`;
    }
    if (!beyond && low.row === rows[0] && low.requiredChestCm < Fit.garmentValue(rows[0], 'chest') - 3) note += ` ${low.row.size} is the smallest listed size and may be loose. Check the garment chest before choosing it.`;
    return { rows: matches, lowChestCm: low.requiredChestCm, highChestCm: high.requiredChestCm, requiredWaistCm: low.requiredWaistCm, easeCm: low.easeCm, measured, beyond, note };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { recommendProduct, convertMeasurement };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('fitwarm-product-size')) return;

  class FitwarmProductSize extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      try {
        this.chart = JSON.parse(this.querySelector('[data-fit-charts]').textContent)[this.dataset.category];
        this.product = JSON.parse(this.querySelector('[data-fit-product]').textContent);
        this.sizeIndex = Number(this.dataset.sizeIndex);
        if (!this.chart || !this.product.variants.some(variant => Fit.normalizeSize(variant.options[this.sizeIndex]))) return;
      } catch (_) { return; }
      this.abort = new AbortController();
      this.section = this.closest('[data-section-type="product"]');
      this.form = this.querySelector('[data-fit-form]');
      this.unit = this.form.elements.fit_weight_unit.value || this.dataset.weightUnit || 'lb';
      this.lengthUnit = this.form.elements.fit_length_unit.value || this.dataset.lengthUnit || 'cm';
      this.last = null;
      this.originalInputs = {};
      this.accepted = Object.fromEntries(['weight', 'chest', 'secondary'].map(name => [name, this.form.elements[name].value]));
      for (const name of ['weight', 'chest', 'secondary']) this.rememberInput(this.form.elements[name]);
      this.disclosures = new Map();
      const disclosure = this.querySelector('.fw-product-size__disclosure');
      if (disclosure) this.setupDisclosure(disclosure, disclosure.querySelector('.fw-product-size__panel'));
      const measurements = this.querySelector('[data-fit-measurements]');
      this.setupDisclosure(measurements, this.querySelector('[data-fit-measurement-fields]'));
      this.dialog = this.querySelector('[data-fit-dialog]');
      this.trigger = this.querySelector('[data-fit-open]');
      if (this.dialog) {
        this.listen(this.trigger, 'click', () => this.openDialog());
        this.listen(this.querySelector('[data-fit-close]'), 'click', () => this.closeDialog());
        this.listen(this.dialog, 'keydown', event => {
          if (event.key !== 'Tab') return;
          const controls = [...this.dialog.querySelectorAll('button, a[href], input, select, textarea, summary, [tabindex]')]
            .filter(node => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length > 0);
          const target = event.shiftKey ? controls[controls.length - 1] : controls[0];
          if ((event.shiftKey && document.activeElement === controls[0]) || (!event.shiftKey && document.activeElement === controls[controls.length - 1])) {
            event.preventDefault();
            target?.focus();
          }
        });
        this.listen(this.dialog, 'click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
        });
        this.listen(this.dialog, 'close', () => {
          if (this.dialog.open) return;
          this.releaseDialog();
          const target = this.returnFocus || this.trigger;
          this.returnFocus = null;
          if (target?.isConnected) target.focus({ preventScroll: true });
        });
      }
      if (window.Shopify?.designMode) {
        this.listen(document, 'shopify:block:select', event => {
          if (event.detail?.blockId !== this.dataset.blockId) return;
          if (this.dialog) this.openDialog();
          else this.setDisclosureOpen(disclosure, true);
        });
        this.listen(document, 'shopify:block:deselect', event => {
          if (event.detail?.blockId === this.dataset.blockId) this.closeDialog();
        });
      }
      this.listen(this.form, 'submit', event => { event.preventDefault(); this.calculate(); });
      this.listen(this.form, 'keydown', event => {
        if (event.target.type === 'number' && !event.ctrlKey && !event.metaKey && ['e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
      });
      this.listen(this.form, 'beforeinput', event => {
        if (event.target.type === 'number' && event.data && /[eE+\-]/.test(event.data)) event.preventDefault();
      });
      this.listen(this.form, 'input', event => {
        const field = event.target;
        if (field.type !== 'number') return;
        if (field.validity.badInput || field.value && !/^\d*(?:\.\d*)?$/.test(field.value)) field.value = this.accepted[field.name] || '';
        if (field.value !== '' && (!Number.isFinite(field.valueAsNumber) || field.valueAsNumber > Number(field.max))) field.value = field.max;
        this.accepted[field.name] = field.value;
        this.rememberInput(field);
        field.removeAttribute('aria-invalid');
        this.clearResult();
        this.error('');
      });
      this.listen(this.form, 'paste', event => {
        const field = event.target;
        if (field.type !== 'number') return;
        const text = event.clipboardData?.getData('text').trim();
        if (text === undefined) return;
        event.preventDefault();
        if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) { this.error('Use a number without signs or letters.'); return; }
        field.value = String(Math.min(Number(field.max), Number(text)));
        this.accepted[field.name] = field.value;
        field.removeAttribute('aria-invalid');
        this.rememberInput(field);
        this.clearResult();
        this.error('');
      });
      this.listen(this.form, 'focusout', event => {
        const field = event.target;
        const original = this.originalInputs[field.name];
        const unit = field.name === 'weight' ? this.unit : this.lengthUnit;
        if (original && original.unit !== unit && field.value === convertMeasurement(original.text, original.unit, unit)) return;
        if (field.type === 'number' && field.value !== '' && field.valueAsNumber < Number(field.min)) {
          field.value = field.min;
          this.accepted[field.name] = field.value;
          this.rememberInput(field);
          this.clearResult();
        }
      });
      this.listen(this.form, 'change', event => {
        if (event.target.name === 'fit_weight_unit') this.setUnit(event.target.value, false);
        if (event.target.name === 'fit_length_unit') this.setUnit(event.target.value, true);
      });
      this.listen(this.querySelector('[data-fit-size-actions]'), 'click', event => {
        const button = event.target.closest('[data-fit-use-size]');
        if (button && !button.disabled) this.useSize(button.dataset.fitUseSize);
      });
      if (this.section) {
        this.listen(this.section, 'variantChange', () => this.updateAction());
        this.listen(this.section, 'change', event => { if (event.target.matches('[data-variant-input]')) this.updateAction(); });
      }
      this.updateLimits();
      this.clearResult();
      this.error('');
      this.hidden = false;
    }

    disconnectedCallback() {
      this.disclosures?.forEach((state, details) => {
        state.animation?.cancel();
        state.fade?.cancel();
        details.open = state.expanded;
        delete details.dataset.fitAnimating;
      });
      if (this.dialog?.open) this.dialog.close();
      this.releaseDialog();
      this.abort?.abort();
      this.abort = null;
    }
    openDialog() {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = null;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('fw-product-size-modal-open');
      this.form.elements.weight.focus({ preventScroll: true });
    }
    closeDialog(target = this.trigger) {
      if (!this.dialog?.open) return;
      this.returnFocus = target;
      this.dialog.close();
    }
    releaseDialog() {
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('.fw-product-size__dialog[open]')) document.documentElement.classList.remove('fw-product-size-modal-open');
    }
    listen(target, type, callback) { target.addEventListener(type, callback, { signal: this.abort.signal }); }
    text(selector, value) { this.querySelector(selector).textContent = value; }
    error(message) { this.text('[data-fit-error]', message); this.querySelector('[data-fit-error]').hidden = !message; }
    clearResult() { this.last = null; this.querySelector('[data-fit-result]').hidden = true; }

    setupDisclosure(details, content) {
      if (!details || !content) return;
      const summary = details.querySelector(':scope > summary');
      const state = { summary, content, expanded: details.open };
      this.disclosures.set(details, state);
      details.dataset.fitExpanded = String(state.expanded);
      summary.setAttribute('aria-expanded', String(state.expanded));
      this.listen(summary, 'click', event => {
        event.preventDefault();
        this.setDisclosureOpen(details, !state.expanded);
      });
      this.listen(details, 'toggle', () => {
        if (state.animation) return;
        state.expanded = details.open;
        details.dataset.fitExpanded = String(state.expanded);
        summary.setAttribute('aria-expanded', String(state.expanded));
      });
    }

    setDisclosureOpen(details, expanded) {
      const state = this.disclosures.get(details);
      if (!state) { if (details) details.open = expanded; return; }
      if (state.expanded === expanded && details.open === expanded && !state.animation) return;
      const height = details.getBoundingClientRect().height;
      const opacity = details.open ? getComputedStyle(state.content).opacity : '0';
      state.animation?.cancel();
      state.fade?.cancel();
      state.expanded = expanded;
      details.open = true;
      details.dataset.fitExpanded = String(expanded);
      state.summary.setAttribute('aria-expanded', String(expanded));
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
        state.animation = null;
        details.open = expanded;
        delete details.dataset.fitAnimating;
        return;
      }
      const style = getComputedStyle(details);
      const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const targetHeight = (expanded ? details.scrollHeight : state.summary.getBoundingClientRect().height) + border;
      const options = { duration: expanded ? 260 : 210, easing: 'cubic-bezier(.22,.61,.36,1)' };
      details.dataset.fitAnimating = '';
      const animation = details.animate([{ height: `${height}px` }, { height: `${targetHeight}px` }], options);
      state.animation = animation;
      state.fade = state.content.animate([{ opacity }, { opacity: expanded ? 1 : 0 }], options);
      animation.finished.then(() => {
        if (state.animation !== animation) return;
        state.animation = null;
        details.open = expanded;
        delete details.dataset.fitAnimating;
      }).catch(() => {});
    }

    rememberInput(field) {
      this.originalInputs[field.name] = { text: field.value, unit: field.name === 'weight' ? this.unit : this.lengthUnit };
    }

    updateLimits() {
      const weight = this.form.elements.weight;
      [weight.min, weight.max] = this.unit === 'kg' ? ['0.1', '250'] : ['0.2', '550'];
      weight.placeholder = this.unit === 'kg' ? 'e.g. 3' : 'e.g. 8';
      this.text('[data-fit-weight-label]', this.unit);
      for (const name of ['chest', 'secondary']) {
        const field = this.form.elements[name];
        const inches = name === 'chest' || this.chart.secondary === 'waist' ? [4, 60] : [2, 40];
        const factor = this.lengthUnit === 'cm' ? 2.54 : 1;
        [field.min, field.max] = inches.map(value => String(Number((value * factor).toFixed(2))));
        field.placeholder = this.lengthUnit === 'cm' ? (name === 'chest' ? 'e.g. 34' : 'e.g. 25') : (name === 'chest' ? 'e.g. 13.5' : 'e.g. 10');
      }
      this.querySelectorAll('[data-fit-length-label]').forEach(node => { node.textContent = this.lengthUnit; });
    }

    setUnit(next, length) {
      const old = length ? this.lengthUnit : this.unit;
      if (old === next) return;
      // Always convert from the last user entry, never a rounded display value.
      for (const name of length ? ['chest', 'secondary'] : ['weight']) {
        const field = this.form.elements[name];
        const original = this.originalInputs[name];
        field.value = convertMeasurement(original.text, original.unit, next);
        this.accepted[name] = field.value;
      }
      if (length) this.lengthUnit = next; else this.unit = next;
      this.updateLimits();
      let invalid = null;
      for (const name of length ? ['chest', 'secondary'] : ['weight']) {
        const field = this.form.elements[name];
        field.removeAttribute('aria-invalid');
        if (field.value !== '' && (field.valueAsNumber < Number(field.min) || field.valueAsNumber > Number(field.max))) {
          field.setAttribute('aria-invalid', 'true');
          invalid ||= field;
          this.clearResult();
        }
      }
      this.error(invalid ? this.fieldMessage(invalid) : '');
      if (this.last) this.render();
    }

    validField(field, required) {
      if (field.value === '' && !required && !field.validity.badInput) return true;
      if (field.value === '' || !Number.isFinite(field.valueAsNumber) || field.valueAsNumber < Number(field.min) - 1e-7 || field.valueAsNumber > Number(field.max) + 1e-7) {
        field.setAttribute('aria-invalid', 'true');
        this.error(this.fieldMessage(field));
        if (field.name !== 'weight') this.setDisclosureOpen(this.querySelector('[data-fit-measurements]'), true);
        field.focus();
        return false;
      }
      return true;
    }

    fieldMessage(field) {
      const label = this.form.querySelector(`label[for="${field.id}"]`).textContent.replace(' (optional)', '');
      return `${label}: enter ${field.min}–${field.max} ${field.name === 'weight' ? this.unit : this.lengthUnit}.`;
    }

    readBody() {
      const values = this.originalInputs;
      const cm = input => input.text === '' ? null : Number(input.text) * (input.unit === 'in' ? 2.54 : 1);
      return { weightKg: values.weight.text === '' ? NaN : Number(values.weight.text) / (values.weight.unit === 'lb' ? Fit.LB_PER_KG : 1), chestCm: cm(values.chest), secondaryCm: cm(values.secondary) };
    }

    calculate() {
      this.form.querySelectorAll('[aria-invalid]').forEach(node => node.removeAttribute('aria-invalid'));
      this.clearResult();
      this.error('');
      const values = this.form.elements;
      const measured = values.chest.value !== '';
      if (!this.validField(values.weight, !measured) || !this.validField(values.chest, false) || !this.validField(values.secondary, this.chart.secondary === 'waist')) return;
      try {
        this.last = recommendProduct(this.chart, this.product, this.sizeIndex, { ...this.readBody(), chestVariationPercent: this.dataset.chestVariationPercent });
        this.render();
      } catch (error) { this.error(error.message); }
    }

    render() {
      const result = this.last;
      if (!result) return;
      this.querySelector('[data-fit-result]').hidden = false;
      const multiple = result.rows.length > 1;
      const sizes = result.rows.map(row => row.size);
      const label = sizes.length ? multiple ? `${sizes[0]}–${sizes[sizes.length - 1]}` : sizes[0] : 'Outside this chart';
      this.text('[data-fit-result-heading]', result.beyond ? 'Confirm measurements' : result.measured ? 'Suggested size' : multiple ? 'Estimated size range' : 'Estimated size');
      this.text('[data-fit-size]', sizes.length && result.beyond ? `${label}+` : label);
      this.text('[data-fit-note]', result.note);
      const column = this.lengthUnit === 'cm' ? 1 : 0;
      const dimensions = this.querySelector('[data-fit-dimensions]');
      dimensions.replaceChildren(...result.rows.map(row => {
        const line = document.createElement('p');
        line.textContent = `${multiple ? `${row.size} · ` : ''}Garment chest: ${row.chest[column]} ${this.lengthUnit} · ${this.chart.secondary === 'waist' ? 'waist' : 'back'}: ${row[this.chart.secondary][column]} ${this.lengthUnit}`;
        return line;
      }));
      const actions = this.querySelector('[data-fit-size-actions]');
      actions.hidden = result.beyond || !sizes.length;
      actions.replaceChildren(...(result.beyond ? [] : sizes).map(size => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fw-product-size__select-size';
        button.dataset.fitUseSize = size;
        return button;
      }));
      this.updateAction();
    }

    selectedOptions() {
      return this.product.options.map((_, index) => {
        const controls = this.section?.querySelectorAll(`[data-variant-input][data-index="option${index + 1}"]`) || [];
        return [...controls].find(input => input.tagName === 'SELECT' || input.checked)?.value || '';
      });
    }

    updateAction() {
      if (!this.last || this.last.beyond) { this.text('[data-fit-stock]', ''); return; }
      const options = this.selectedOptions();
      const unavailable = [];
      this.querySelectorAll('[data-fit-use-size]').forEach(button => {
        const size = button.dataset.fitUseSize;
        const variant = Fit.matchingVariant(this.product.variants, this.sizeIndex, size, options);
        const selected = Fit.normalizeSize(options[this.sizeIndex]) === size;
        const available = Boolean(variant?.available);
        const control = variant && this.sizeControl(size, variant);
        button.disabled = !available || selected || !control;
        button.textContent = !available ? `${size} unavailable` : selected ? `${size} selected` : `Select ${size}`;
        if (!available) unavailable.push(size);
      });
      this.text('[data-fit-stock]', unavailable.length ? `${unavailable.join(', ')} unavailable in the selected color. Check another color or style.` : 'Available in the selected color.');
    }

    sizeControl(size, variant) {
      const controls = this.section?.querySelectorAll(`[data-variant-input][data-index="option${this.sizeIndex + 1}"]`) || [];
      return [...controls].find(input => !input.disabled && (input.tagName === 'SELECT' ? [...input.options].some(option => !option.disabled && option.value === variant.options[this.sizeIndex]) : Fit.normalizeSize(input.value) === size));
    }

    useSize(size) {
      if (!this.last || this.last.beyond || !this.last.rows.some(row => row.size === size)) return;
      const options = this.selectedOptions();
      const variant = Fit.matchingVariant(this.product.variants, this.sizeIndex, size, options);
      if (!variant?.available) { this.updateAction(); return; }
      const input = this.sizeControl(size, variant);
      if (!input) return;
      if (input.tagName === 'SELECT') {
        input.value = variant.options[this.sizeIndex];
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else input.click();
      this.updateAction();
      this.closeDialog(input);
    }
  }
  customElements.define('fitwarm-product-size', FitwarmProductSize);
})();
